import { useEffect, useState } from 'react';
import Breadcrumb from '../../Breadcrumb/Breadcrumb';
import axios from '../../../axios';
import usePermisos from '../../../hooks/usePermisos';
import NoPermissions from '../../NoPermissions/NoPermissions';
import { Table, useTablePagination, useTableSort } from '../../UI';
import { Dialog, DialogPanel, DialogTitle } from '@headlessui/react';

const PERMISOS_REQUERIDOS = [
    'patient_create',
    'patient_read',
    'patient_update',
    'patient_delete'
];

const Clientes = () => {
    const { permisos, loading: loadingPermisos, tienePermiso } = usePermisos(PERMISOS_REQUERIDOS);
    const [clientes, setClientes] = useState([]);
    const [clientesRaw, setClientesRaw] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [filtroFecha, setFiltroFecha] = useState({ fechaInicio: '', fechaFin: '' });
    const [modalFechaOpen, setModalFechaOpen] = useState(false);
    const [loader, setLoader] = useState(true);

    // Hooks para sorting y paginación
    const { sortedData, sortConfig, handleSort } = useTableSort(clientes, { field: '', direction: 'ASC' });
    const {
        paginatedData,
        page,
        setPage,
        itemsPerPage,
        setItemsPerPage,
        totalItems,
        totalPages,
    } = useTablePagination(sortedData, { page: 1, itemsPerPage: 25 });

    useEffect(() => {
        fetchClientes();
    }, []);

    const fetchClientes = async () => {
        try {
            const res = await axios.get('api/patients');
            setClientes(res.data);
            setClientesRaw(res.data);
            setLoader(false);
        } catch (e) {
            console.error('Error al obtener clientes:', e);
            setLoader(false);
        }
    };

    const handleSearch = (e) => {
        const value = e.target.value.toLowerCase();
        setSearchTerm(value);
        
        if (value.length > 0) {
            const filtrados = clientesRaw.filter((item) =>
                item.nombre?.toLowerCase().includes(value)
            );
            setClientes(filtrados);
        } else {
            setClientes(clientesRaw);
        }
    };

    const handleSearchByDate = () => {
        if (filtroFecha.fechaInicio && filtroFecha.fechaFin) {
            const fInicio = new Date(filtroFecha.fechaInicio).getTime();
            const fFin = new Date(filtroFecha.fechaFin).getTime();
            const filtrados = clientesRaw.filter((item) => {
                const fNacimiento = new Date(item.fecha_nacimiento).getTime();
                return fNacimiento >= fInicio && fNacimiento <= fFin;
            });
            setClientes(filtrados);
            setModalFechaOpen(false);
            setFiltroFecha({ fechaInicio: '', fechaFin: '' });
        }
    };

    const handleResetFilters = () => {
        setSearchTerm('');
        setFiltroFecha({ fechaInicio: '', fechaFin: '' });
        setClientes(clientesRaw);
    };

    const handleDelete = async (cliente) => {
        if (window.confirm('¿Realmente deseas borrar a este paciente?')) {
            try {
                const res = await axios.delete(`api/patients/${cliente.id}`);
                alert(res.data.message || 'Paciente eliminado exitosamente');
                setClientes((prev) => prev.filter((u) => u.id !== res.data.paciente.id));
                setClientesRaw((prev) => prev.filter((u) => u.id !== res.data.paciente.id));
            } catch (e) {
                console.error(e);
                alert(e.response?.data?.message || 'Error al eliminar el paciente');
            }
        }
    };

    // Columnas para la tabla
    const columns = [
        { 
            key: 'id', 
            label: 'ID', 
            sortable: true,
            render: (value, row) => (
                <div className="flex items-center gap-2">
                    <div className="w-8 h-8 rounded-full border-2 border-white shadow-md overflow-hidden shrink-0">
                        <img
                            className="w-full h-full object-cover"
                            src="/images/paciente.png"
                            alt="Paciente"
                        />
                    </div>
                    <span>{value}</span>
                </div>
            )
        },
        { key: 'nombre', label: 'Nombres', sortable: true },
        { 
            key: 'orders', 
            label: 'Pedidos',
            sortable: true,
            render: (value, row) => row.orders?.length || 0
        },
        { key: 'cp', label: 'CP', sortable: true },
        { key: 'ciudad', label: 'Ciudad', sortable: true },
        { key: 'fecha_nacimiento', label: 'Fecha Nacimiento', sortable: true },
        { key: 'telefono', label: 'Teléfono' },
        { key: 'celular', label: 'Celular' },
    ];

    // Acciones para la tabla
    const actions = [
        {
            type: 'edit',
            to: (row) => `${row.id}`,
            tooltip: 'Editar',
            permission: 'patient_update',
        },
        {
            type: 'delete',
            onClick: handleDelete,
            tooltip: 'Eliminar',
            permission: 'patient_delete',
        },
    ];

    if (loadingPermisos) {
        return (
            <div>
                <div className='container-admin-menu'>
                    <Breadcrumb hasButton={false} hasFilters={false} />
                </div>
                <div className='container-admin-container'>
                    <div className='container-admin z-depth-1 p-4'>
                        <div className="flex justify-center py-10">
                            <span className="text-gray-500">Cargando...</span>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    if (!tienePermiso('patient_read')) {
        return (
            <div>
                <div className='container-admin-menu'>
                    <Breadcrumb hasButton={tienePermiso('patient_create')} hasFilters={false} />
                </div>
                <div className='container-admin-container'>
                    <div className='container-admin z-depth-1'>
                        <NoPermissions />
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div>
            <div className="container-admin-menu">
                <Breadcrumb hasButton={tienePermiso('patient_create')} hasFilters={false} />
            </div>
            <div className="container-admin-container">
                <div className="container-admin z-depth-1 p-4">
                    {/* Modal para buscar por fecha */}
                    <Dialog open={modalFechaOpen} onClose={() => setModalFechaOpen(false)} className="relative z-50">
                        <div className="fixed inset-0 bg-black/30" aria-hidden="true" />
                        <div className="fixed inset-0 flex items-center justify-center p-4">
                            <DialogPanel className="mx-auto max-w-lg rounded-lg bg-white p-6 shadow-xl">
                                <h4 className="text-lg font-bold mb-4">Buscar por fecha de nacimiento</h4>
                                <div className="space-y-4">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            Fecha de inicio
                                        </label>
                                        <input
                                            type="date"
                                            className="dento-input"
                                            value={filtroFecha.fechaInicio}
                                            onChange={(e) => setFiltroFecha({ ...filtroFecha, fechaInicio: e.target.value })}
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            Fecha final
                                        </label>
                                        <input
                                            type="date"
                                            className="dento-input"
                                            value={filtroFecha.fechaFin}
                                            onChange={(e) => setFiltroFecha({ ...filtroFecha, fechaFin: e.target.value })}
                                        />
                                    </div>
                                </div>
                                <div className="flex justify-end gap-2 mt-6">
                                    <button
                                        className="px-4 py-2 text-gray-700 bg-gray-200 rounded-lg hover:bg-gray-300 transition-colors"
                                        onClick={() => setModalFechaOpen(false)}
                                    >
                                        Cancelar
                                    </button>
                                    <button
                                        className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                                        onClick={handleSearchByDate}
                                    >
                                        Aplicar Filtro
                                    </button>
                                </div>
                            </DialogPanel>
                        </div>
                    </Dialog>

                    {/* Barra de búsqueda */}
                    <div className="info-badge rounded-xl p-3 mb-4">
                        <div className="flex flex-col md:flex-row items-center gap-3">
                            {/* Búsqueda por nombre */}
                            <div className="flex items-center gap-2 flex-1">
                                <img
                                    className="w-8 h-8"
                                    src="/images/pacientes.png"
                                    alt="Pacientes"
                                />
                                <span className="text-white font-medium text-sm whitespace-nowrap">Nombres:</span>
                                
                                <div className="relative flex-1">
                                    <input
                                        type="text"
                                        className="w-full px-4 py-2 pr-10 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                                        placeholder="Buscar por: Nombres | Apellidos"
                                        value={searchTerm}
                                        onChange={handleSearch}
                                    />
                                    <svg
                                        className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400"
                                        fill="none"
                                        stroke="currentColor"
                                        viewBox="0 0 24 24"
                                    >
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                                    </svg>
                                </div>
                            </div>

                            {/* Búsqueda por fecha */}
                            <div className="flex items-center gap-2">
                                <img
                                    className="w-8 h-8"
                                    src="/images/calendar2.png"
                                    alt="Calendario"
                                />
                                <span className="text-white font-medium text-sm whitespace-nowrap">F. de nacimiento:</span>
                                
                                <button
                                    onClick={() => setModalFechaOpen(true)}
                                    className="px-4 py-2 bg-white text-gray-700 rounded-lg hover:bg-gray-100 transition-colors text-sm font-medium"
                                >
                                    Buscar...
                                </button>
                            </div>

                            {/* Botón limpiar filtros */}
                            {(searchTerm || filtroFecha.fechaInicio || filtroFecha.fechaFin) && (
                                <button
                                    onClick={handleResetFilters}
                                    className="px-4 py-2 bg-white text-gray-700 rounded-lg hover:bg-gray-100 transition-colors text-sm font-medium flex items-center gap-1"
                                    title="Limpiar búsqueda"
                                >
                                    <img src="/images/clear-filters.png" alt="Limpiar" className="w-4 h-4" />
                                    Limpiar
                                </button>
                            )}
                        </div>
                    </div>

                    <Table
                        columns={columns}
                        data={paginatedData}
                        actions={actions}
                        permissions={permisos}
                        loading={loader}
                        striped={true}
                        hoverable={true}
                        emptyMessage="No hay pacientes registrados"
                        sorting={{
                            field: sortConfig.field,
                            direction: sortConfig.direction,
                            onSort: handleSort
                        }}
                        pagination={{
                            page: page,
                            totalItems: totalItems,
                            itemsPerPage: itemsPerPage,
                            onPageChange: setPage,
                            onItemsPerPageChange: setItemsPerPage,
                            itemsPerPageOptions: [10, 25, 50, 100]
                        }}
                    />
                </div>
            </div>
        </div>
    );
};

export default Clientes;